import { Controller, FormProvider, useFormContext } from 'react-hook-form';

import {
  BusinessUnitIntegrationSetting,
  IntegrationSettings,
} from '@xemplo/business-unit-query';
import { ButtonType, ButtonVariant } from '@xemplo/button';
import { HeaderIconMode } from '@xemplo/header-icon';
import { DawnSettings } from '@xemplo/icons';
import { Modal, ModalDialogSlot } from '@xemplo/modal-dialog';
import { Toggle, ToggleSize } from '@xemplo/switch';

import { SettingDefinition, SETTINGS_SECTIONS } from '../business-unit-settings.config';
import * as S from '../business-unit-settings.styles';
import { IntegrationSettingsFormValues } from '../business-unit-settings.types';

import { useEditSettingsModal } from './use-edit-settings-modal';

function SettingToggle({ setting }: Readonly<{ setting: SettingDefinition }>) {
  const { control } = useFormContext<IntegrationSettingsFormValues>();

  return (
    <S.SettingRow>
      <S.SettingLabel>
        <S.Label>{setting.label}</S.Label>
        {setting.helperText && <S.HelperText>{setting.helperText}</S.HelperText>}
      </S.SettingLabel>
      <Controller
        name={setting.settingName}
        control={control}
        render={({ field }) => (
          <Toggle
            id={`toggle-${setting.settingName}`}
            value={setting.settingName}
            ariaLabel={setting.label}
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            size={ToggleSize.Standard}
          />
        )}
      />
    </S.SettingRow>
  );
}

function SettingsForm() {
  return (
    <S.Sections>
      {SETTINGS_SECTIONS.map((section) => (
        <S.Section key={section.id}>
          <S.SectionHeading>
            <S.SectionTitle>{section.title}</S.SectionTitle>
            <S.SectionDescription>{section.description}</S.SectionDescription>
          </S.SectionHeading>

          <S.SettingsGroup>
            {section.settings.map((setting) => (
              <SettingToggle key={setting.settingName} setting={setting} />
            ))}
          </S.SettingsGroup>
        </S.Section>
      ))}
    </S.Sections>
  );
}

export type EditSettingsContentProps = {
  businessUnitId: string;
  settings: IntegrationSettings;
  /** Raw GET rows — carried to submit time as the full-replace write source of truth. */
  originalRows: BusinessUnitIntegrationSetting[];
  onClose: () => void;
};

export function EditSettingsContent(props: Readonly<EditSettingsContentProps>) {
  const { methods, handleSubmit, handleClose, isDirty } = useEditSettingsModal(props);

  return (
    <FormProvider {...methods}>
      <Modal>
        <Modal.Slot
          name={ModalDialogSlot.CloseButton}
          render={
            <Modal.CloseButton
              onClick={handleClose}
              id="edit-business-unit-settings-close-button"
              ariaLabel="close modal"
            />
          }
        />
        <Modal.Slot
          name={ModalDialogSlot.Header}
          render={
            <Modal.Header
              title="Settings"
              headerIcon={{ icon: <DawnSettings />, mode: HeaderIconMode.Object }}
            />
          }
        />
        <Modal.Slot
          name={ModalDialogSlot.Body}
          render={
            <Modal.Body>
              <SettingsForm />
            </Modal.Body>
          }
        />
        <Modal.Slot
          name={ModalDialogSlot.Footer}
          render={
            <Modal.Footer
              btnPrimary={{
                onClickAsync: handleSubmit,
                label: 'Save',
                ariaLabel: 'Save settings button',
                type: ButtonType.Primary,
                disabled: !isDirty,
              }}
              btnTertiary={{
                onClick: handleClose,
                label: 'Cancel',
                ariaLabel: 'Cancel edit settings button',
                type: ButtonType.Tertiary,
                variant: ButtonVariant.Dark,
              }}
            />
          }
        />
      </Modal>
    </FormProvider>
  );
}
